import { Plus, Trash2 } from 'lucide-react';
import { pl } from 'date-fns/locale';
import { Label } from '@ooh/ui/components/label';
import { Input } from '@ooh/ui/components/input';
import { Button } from '@ooh/ui/components/button';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption as SelectOption,
} from '@ooh/ui/components/searchable-select-field';
import { DateRangeButtonField } from '@ooh/ui/components/date-range-button-field';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';

export interface MonthPeriodRow {
  id: string;
  /** Logical period label (row identity) — not itself re-mappable, unlike sourceColumn. */
  month: string;
  /** Auto-detected file column feeding this period — editable in case detection picked the wrong column. */
  sourceColumn: string;
  from: string;
  to: string;
}

export interface MonthPeriodColumnsTableLabels {
  sectionLabel: string;
  periodColumnLabel: string;
  sourceColumnLabel: string;
  dateRangeColumnLabel: string;
  /** Only needed when `onUpdateYear` is passed (renders the year column). */
  yearColumnLabel?: string;
  dateRangePlaceholder?: string;
  /** Only needed when `onAddRow` is passed. */
  addRowButtonLabel?: string;
  /** Only needed when `onRemoveRow` is passed. */
  removeRowLabel?: string;
  /** Only needed when `onAddRow` is passed and `rows` is empty. */
  emptyStateText?: string;
}

export interface MonthPeriodColumnsTableProps {
  rows: MonthPeriodRow[];
  columnOptions: SelectOption[];
  onUpdateRow: (id: string, patch: Partial<Omit<MonthPeriodRow, 'id' | 'month'>>) => void;
  /**
   * Recomputes one row's `from`/`to` for a resolver-guessed year that turned
   * out wrong (see availability-profile-resolution's month-matrix year
   * disambiguation rule) — keeps the row's month/half, changes only the year.
   *
   * Omit for a manually-built row (via `onAddRow`): there is no auto-detected
   * month/half to preserve, so the date-range picker alone already lets the
   * operator pick the exact year — a separate "ROK" column there would be a
   * second control for the same value. Omitting `onUpdateYear` hides the
   * entire year column (header + per-row input).
   */
  onUpdateYear?: (id: string, nextYear: number) => void;
  labels: MonthPeriodColumnsTableLabels;
  testIdPrefix?: string;
  /**
   * Appends a new, blank period row (source column + date range left for the
   * operator to fill in via the same editable cells every other row uses).
   * Omit to keep the table showing only resolver-detected rows (the original
   * behavior, still used by the real preview phase's month-matrix path) —
   * passing it enables manual from-scratch period-column construction for a
   * file whose period headers don't auto-parse (e.g. Roman-numeral month
   * abbreviations, CU-86cb1npz2). Also renders an empty-state row (using
   * `labels.emptyStateText`) when `rows` is empty.
   */
  onAddRow?: () => void;
  /** Removes one manually-added row. Omit to hide the per-row delete action. */
  onRemoveRow?: (id: string) => void;
}

/**
 * Editable month/half-month period-matrix mapping: which file column feeds
 * each detected period, and that period's actual start/end date. Auto-fill
 * can misjudge a fiscal-month boundary or mis-map which column is which
 * month, so every cell stays editable rather than a static summary.
 */
/** Extracts the resolved year (`YYYY`) from a row's `from` date, or '' when unset. */
function rowYear(from: string): string {
  return from.length >= 4 ? from.slice(0, 4) : '';
}

/**
 * Parses the year input's raw string value into a valid 4-digit year, or
 * `null` when the value is a partial year or not an integer at all.
 * Extracted as a pure function (rather than inlined in the onChange handler)
 * so it can be unit-tested directly — `<input type="number">` sanitizes
 * non-numeric keystrokes at the DOM level before `onChange` ever fires,
 * making the non-numeric branch unreachable through a simulated DOM event.
 */
export function parseYearInput(raw: string): number | null {
  if (raw.length !== 4) return null;
  const year = Number(raw);
  return Number.isInteger(year) ? year : null;
}

export function MonthPeriodColumnsTable({
  rows,
  columnOptions,
  onUpdateRow,
  onUpdateYear,
  labels,
  testIdPrefix = 'month-period-columns',
  onAddRow,
  onRemoveRow,
}: MonthPeriodColumnsTableProps) {
  const showActionsColumn = onRemoveRow !== undefined;
  const showYearColumn = onUpdateYear !== undefined;
  const columnCount = 3 + (showYearColumn ? 1 : 0) + (showActionsColumn ? 1 : 0);

  return (
    <div className="space-y-1.5">
      <Label>{labels.sectionLabel}</Label>
      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{labels.periodColumnLabel}</TableHead>
              <TableHead>{labels.sourceColumnLabel}</TableHead>
              <TableHead>{labels.dateRangeColumnLabel}</TableHead>
              {showYearColumn ? <TableHead>{labels.yearColumnLabel}</TableHead> : null}
              {showActionsColumn ? <TableHead aria-hidden /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && onAddRow ? (
              <TableRow listRow>
                <TableCell
                  colSpan={columnCount}
                  className="px-4 py-4 text-center text-xs text-muted-foreground italic"
                  data-testid={`${testIdPrefix}-empty-state`}
                >
                  {labels.emptyStateText}
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((row) => (
              <TableRow key={row.id} listRow>
                <TableCell className="px-4 py-2 text-[13px] font-medium">{row.month}</TableCell>
                <TableCell className="px-4 py-2">
                  <SearchableSelectField
                    options={columnOptions}
                    value={row.sourceColumn}
                    onChange={(next) => onUpdateRow(row.id, { sourceColumn: next })}
                    data-testid={`${testIdPrefix}-column-${row.id}`}
                  />
                </TableCell>
                <TableCell className="px-4 py-2">
                  <DateRangeButtonField
                    fromValue={row.from}
                    toValue={row.to}
                    onChange={(next) => onUpdateRow(row.id, next)}
                    placeholder={labels.dateRangePlaceholder}
                    locale={pl}
                    testId={`${testIdPrefix}-range-${row.id}`}
                  />
                </TableCell>
                {showYearColumn ? (
                  <TableCell className="px-4 py-2">
                    <Input
                      type="number"
                      className="w-20"
                      value={rowYear(row.from)}
                      onChange={(e) => {
                        const nextYear = parseYearInput(e.target.value);
                        if (nextYear !== null) onUpdateYear(row.id, nextYear);
                      }}
                      data-testid={`${testIdPrefix}-year-${row.id}`}
                    />
                  </TableCell>
                ) : null}
                {showActionsColumn ? (
                  <TableCell className="px-4 py-2">
                    <IconButtonWithTooltip
                      icon={Trash2}
                      tooltip={labels.removeRowLabel ?? ''}
                      onClick={() => onRemoveRow(row.id)}
                      testId={`${testIdPrefix}-remove-${row.id}`}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {onAddRow ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAddRow}
          data-testid={`${testIdPrefix}-add-row`}
        >
          <Plus className="h-3.5 w-3.5" />
          {labels.addRowButtonLabel}
        </Button>
      ) : null}
    </div>
  );
}
